// cSpell:ignore Stap Volgende Vorige
import createPaymentSchemas from "@rentiohq/shared-frontend/dist/forms/createPayment";
import {
  isPerformingSubmitActionSelector,
  performingSubmitActionErrorSelector,
  submitActionCreator,
} from "@rentiohq/shared-frontend/dist/forms/createPayment/schema.createPayment.actions";
import { IExtraData } from "@rentiohq/shared-frontend/dist/forms/createPayment/schema.createPayment.types";
import * as authHooks from "@rentiohq/shared-frontend/dist/redux/auth/auth.hooks";
import * as brokerHooks from "@rentiohq/shared-frontend/dist/redux/broker/broker.hooks";
import { generateFormId } from "@rentiohq/shared-frontend/dist/redux/form/form.utils";
import { IGeneralLedgerAccount } from "@rentiohq/shared-frontend/dist/reduxV2/generalLedgerAccount/generalLedgerAccount.types";
import { useLatePaymentPenaltyConfig } from "@rentiohq/shared-frontend/dist/reduxV2/preferences/preference.hooks";
import { EBrokerFeature } from "@rentiohq/shared-frontend/dist/types/broker.types";
import { IContract } from "@rentiohq/shared-frontend/dist/types/contract.types";
import { IPaymentOrder } from "@rentiohq/shared-frontend/dist/types/payment.types";
import { IProperty } from "@rentiohq/shared-frontend/dist/types/property.types";
import {
  Card,
  Loading,
  MultiStepForm as Form,
} from "@rentiohq/web-shared/dist/components";
import { useInternalMode } from "@rentiohq/web-shared/dist/redux/system/system.hooks";
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";

const fallbackFormId = generateFormId();

interface IProps {
  property: IProperty;
  activeContracts?: IContract[];
  endedContracts?: IContract[];
  generalLedgerAccounts?: IGeneralLedgerAccount[];
  paymentOrder?: IPaymentOrder;
  paymentOrders?: IPaymentOrder[];
  hasOpenPaymentRequests?: boolean;
  shouldUpdateOpenPaymentRequests?: boolean;
  prefill?: IExtraData["prefill"];
  // Render the whole flow inside a modal (popup) instead of inline on a page.
  asModal?: boolean;
  // Drop the surrounding <Card> when embedded as a step in another wizard
  // (cost modal) so it doesn't read as "a new screen inside a screen".
  noCard?: boolean;
  // Override the redux form id (defaults to a single module-level id). The
  // cost-modal passes one in so it can subscribe to the inner step state via
  // `currentStepIndexSelector` and show a unified "Stap X / Y" counter.
  formId?: string | number;
  // Called once we know the active schema count (steps in the payment flow).
  // Lets the cost-modal compute total steps for the unified counter.
  onSchemaCount?: (count: number) => void;
  // Imperative handle on the inner MultiStepForm so the embedding wizard can
  // drive Volgende/Vorige from its own footer. Only used together with
  // `noCard` (we hide the inner controls in that mode).
  formRef?: React.Ref<any>;
  // Called when the user presses "Vorige" on the inner form's first step —
  // gives the embedding wizard a chance to step backwards in *its* flow.
  onFirstStepPrev?: () => void;
  // Fires whenever the active inner-step changes. The embedding wizard uses
  // this to know when we're on the inner summary (where the footer button must
  // submit instead of advance).
  onStepChange?: (stepIndex: number) => void;
  // Fires with the visible step titles + active position so the embedding
  // wizard can mirror the payment flow in its own step navigation.
  onVisibleStepsChange?: (
    steps: { title: string; rawIndex: number }[],
    activeVisibleIndex: number,
  ) => void;
  onClose?: () => void;
  // Fires when the create/update request fails.
  onError?: (error: unknown) => void;
  // Runs before the form sends anything: false stops it (see
  // IFormProps.beforeSubmit), and the embedder says why.
  beforeSubmit?: () => Promise<boolean>;
  // Shown above every step, e.g. a warning the embedding flow needs read.
  intro?: React.ReactNode;
  // Shown above the summary only, e.g. a recap of what the steps that are
  // not asked will create.
  summaryIntro?: React.ReactNode;
}

export const PaymentForm = (props: IProps) => {
  const {
    property,
    paymentOrder,
    hasOpenPaymentRequests,
    shouldUpdateOpenPaymentRequests,
    activeContracts,
    endedContracts,
    generalLedgerAccounts,
    paymentOrders,
    prefill,
    asModal,
    noCard,
    formId: formIdProp,
    onSchemaCount,
    formRef,
    onFirstStepPrev,
    onStepChange,
    onVisibleStepsChange,
    onClose,
    onError,
    beforeSubmit,
    intro,
    summaryIntro,
  } = props;

  const [isOnSummary, setIsOnSummary] = React.useState(false);

  const navigate = useNavigate();
  const {
    data: latePaymentPenaltyConfig,
    isFetching: isFetchingLatePaymentPenaltyConfig,
    hasFeature: hasLatePaymentPenaltyForBroker,
  } = useLatePaymentPenaltyConfig();

  const { isBroker } = authHooks.useSelf();

  const hasIbanisationForBroker =
    brokerHooks.useBrokerFeature(EBrokerFeature.Ibanisation) || !isBroker;
  const hasBookkeepingForBroker = brokerHooks.useBrokerFeature(
    EBrokerFeature.Bookkeeping,
  );

  const { internalModeEnabled } = useInternalMode();

  const handleSubmit = () => {
    if (onClose) {
      onClose();
      return;
    }
    navigate(`/properties/${property.id}/payments`);
  };

  const extraData: IExtraData = {
    activeContracts,
    endedContracts,
    generalLedgerAccounts,
    paymentOrders,
    paymentOrder,
    isBroker,
    property,
    hasOpenPaymentRequests,
    shouldUpdateOpenPaymentRequests,
    ibanisationEnabled: hasIbanisationForBroker,
    bookkeepingEnabled: hasBookkeepingForBroker,
    internalModeEnabled,
    prefill,
    latePaymentPenaltyConfig,
    latePaymentPenaltyFeatureEnabled: hasLatePaymentPenaltyForBroker,
  };

  const schemas = useMemo(
    () => createPaymentSchemas(extraData),
    // The schema list only changes when the form's inputs do. We re-compute it
    // when the prefill toggles cost-payment branches (steps appear/disappear).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      hasBookkeepingForBroker,
      hasIbanisationForBroker,
      internalModeEnabled,
      latePaymentPenaltyConfig,
      hasLatePaymentPenaltyForBroker,
      prefill?.costPayment,
      paymentOrder?.id,
    ],
  );

  // Let the embedder count schemas for a unified step counter.
  React.useEffect(() => {
    onSchemaCount?.(schemas.length);
  }, [onSchemaCount, schemas.length]);

  const formId = formIdProp ?? `payment-form-${fallbackFormId}`;

  // The summary is the step after the last schema.
  const handleStepChange = (stepIndex: number) => {
    setIsOnSummary(stepIndex === schemas.length);
    onStepChange?.(stepIndex);
  };

  // MultiStepForm snapshots its schemas into redux once, in its constructor. Anything the
  // schemas read from extraData is therefore frozen at mount, so mounting before the late
  // payment penalty config has arrived would leave "Apply penalty" permanently hidden.
  if (isFetchingLatePaymentPenaltyConfig) {
    return <Loading />;
  }

  const form = (
    <Form
      ref={formRef}
      formId={formId}
      schemas={schemas}
      withSummary={true}
      // When embedded as a step in a parent wizard (cost-modal) the parent's
      // own step counter is authoritative; hide the inner FormAside so it
      // doesn't compete visually. Same for the built-in Volgende/Vorige
      // controls — the parent renders them in its single modal footer.
      withAside={!noCard}
      showControls={!noCard}
      onPressPreviousFirstStep={onFirstStepPrev}
      onStepChange={handleStepChange}
      intro={
        intro || (isOnSummary && summaryIntro) ? (
          <>
            {intro}
            {isOnSummary && summaryIntro}
          </>
        ) : undefined
      }
      onVisibleStepsChange={onVisibleStepsChange}
      activeStep={paymentOrder ? "summary" : undefined}
      asModal={asModal}
      modalProps={
        asModal
          ? { onClose: onClose ?? (() => undefined), width: "large" }
          : undefined
      }
      isPerformingSubmitActionSelector={isPerformingSubmitActionSelector(
        paymentOrder,
        true,
      )}
      performingSubmitActionErrorSelector={performingSubmitActionErrorSelector(
        paymentOrder,
        true,
      )}
      submitActionCreator={(formData: any) => {
        const { id: propertyId } = property;
        const { id: paymentOrderId = undefined } = paymentOrder || {};

        return submitActionCreator(
          extraData,
          propertyId,
          paymentOrderId,
          paymentOrder,
          true,
        )(formData);
      }}
      onSubmit={handleSubmit}
      onError={onError}
      beforeSubmit={beforeSubmit}
    />
  );

  if (asModal) {
    return form;
  }

  if (noCard) {
    return form;
  }

  return <Card>{form}</Card>;
};
